/**
 * Which sign-in the app uses, chosen once at build time.
 *
 * With features.useBffSession (set in the PoC build only), sign-in moves to the
 * AgentCore front door. The browser is sent to /api/auth/login, the front door
 * runs the OAuth flow itself (state + PKCE) and keeps the tokens server-side,
 * and all the browser ever holds is an httpOnly session cookie it cannot read.
 * Every other build keeps oidc-client-ts (react-oidc-context) exactly as before.
 *
 * Components import useAuth from here rather than from react-oidc-context, and
 * get the same shape in both modes (isLoading, isAuthenticated, error,
 * user.profile.email, signinRedirect), so none of them need to know which
 * mode they are in.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";
import config from "../config/apiConfig";

export const bffSessionEnabled = Boolean(config.features?.useBffSession);

// SHA-256 of an empty body. CloudFront's origin access control signs POSTs to
// the Lambda function URL, and Lambda refuses unsigned payloads, so even a POST
// with no body must carry the hash. See sha256Hex in adapters/apiChat.js.
const EMPTY_BODY_SHA256 =
  "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855";

const BffAuthContext = createContext(null);

/** Hand the browser to the front door, which takes it on to Cognito. */
function signinRedirect() {
  window.location.assign("/api/auth/login");
  // Never settles: the page is navigating away.
  return new Promise(() => {});
}

/**
 * End the server-side session. The front door revokes the refresh token,
 * deletes the session and clears the cookie.
 *
 * Awaited by the caller, so the request is not cancelled by the navigation
 * that follows it. Best-effort, because a failure here must never leave
 * somebody unable to sign out of the page. A no-op in oidc-client-ts builds.
 */
export async function endServerSession() {
  if (!bffSessionEnabled) return;
  try {
    await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "same-origin",
      headers: { "x-amz-content-sha256": EMPTY_BODY_SHA256 },
    });
  } catch (err) {
    console.warn("Server sign-out failed", err);
  }
}

export function BffAuthProvider({ children }) {
  const [state, setState] = useState({
    isLoading: true,
    isAuthenticated: false,
    user: null,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    // The cookie is httpOnly, so asking the server is the only way to learn
    // whether there is a session and whose it is.
    fetch("/api/auth/me", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Session check failed (${res.status})`);
        return res.json();
      })
      .then((me) => {
        if (cancelled) return;
        setState({
          isLoading: false,
          isAuthenticated: me.authenticated === true,
          // The shape oidc-client-ts exposes, so auth.user?.profile?.email
          // keeps working unchanged.
          user: me.authenticated
            ? { profile: { email: me.email ?? undefined, sub: me.sub } }
            : null,
          error: null,
        });
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ isLoading: false, isAuthenticated: false, user: null, error });
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => ({ ...state, signinRedirect }), [state]);

  return <BffAuthContext.Provider value={value}>{children}</BffAuthContext.Provider>;
}

function useBffAuth() {
  const context = useContext(BffAuthContext);
  if (!context) throw new Error("useAuth() must be used inside <BffAuthProvider>");
  return context;
}

// Picked once when the module loads, never per render, so every component
// calls the same hook on every render.
export const useAuth = bffSessionEnabled ? useBffAuth : useOidcAuth;
